import React from "react";
import { Box, Typography, Switch } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { COLORS } from "../assets/theme";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import Actions from "../components/Actions";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomLabel from "../components/CustomLabel";
import CustomDialog from "../components/CustomDialog";
import CustomButton from "../components/CustomButton";
import CustomTextField from "../components/CustomTextField";
import { useParams } from "react-router-dom";
import { getAllBranches } from "../services/BranchesService";
import {
  createBanner,
  deleteBanner,
  fetchBanners,
  toggleBanner,
  updateBanner,
} from "../services/BannersService";

type BannerRow = {
  id: string | number;
  title: string;
  description: string;
  priority?: number;
  status: "Active" | "Inactive";
  isActive: boolean;
  imageUrl?: string;
  raw?: any;
};

const resolveImageUrl = (source: any): string | undefined => {
  if (!source) return undefined;
  return (
    source.imageUrl ||
    source.image_url ||
    source.image?.url ||
    source.image ||
    source.photo ||
    source.thumbnail ||
    source.mediaUrl
  );
};

const coerceBoolean = (value: any): boolean | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.toLowerCase().trim();
    if (["true", "1", "active", "yes"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "inactive", "no"].includes(normalized)) {
      return false;
    }
  }
  return undefined;
};

const mapBannerRecord = (banner: any): BannerRow => {
  const imageUrl = resolveImageUrl(banner);
  const activeFromPayload = coerceBoolean(banner?.is_active);
  const activeFromStatus = coerceBoolean(banner?.status);
  const isActive = activeFromPayload ?? activeFromStatus ?? false;

  const status: "Active" | "Inactive" = isActive ? "Active" : "Inactive";

  return {
    id:
      banner?.id ??
      banner?._id ??
      banner?.banner_id ??
      banner?.uuid ??
      banner?.identifier ??
      banner?.bannerId ??
      Date.now(),
    title: banner?.title ?? banner?.name ?? "",
    description: banner?.description ?? "",
    priority:
      banner?.priority !== undefined
        ? Number(banner.priority)
        : banner?.order ?? banner?.sort_order ?? banner?.sort ?? undefined,
    status,
    isActive,
    imageUrl,
    raw: banner,
  };
};

const pickBannerList = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.banners)) return payload.banners;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.banner)) return payload.banner;
  if (payload.banner && typeof payload.banner === "object") {
    return [payload.banner];
  }
  if (payload.banners && typeof payload.banners === "object") {
    const maybeArray = Object.values(payload.banners).find((value) =>
      Array.isArray(value)
    );
    if (maybeArray) return maybeArray as any[];
  }
  return [];
};

const unwrapBanner = (payload: any) => {
  if (!payload) return null;
  if (payload.banner) return payload.banner;
  if (payload.data?.banner) return payload.data.banner;
  if (payload.bannerData) return payload.bannerData;
  if (payload.data && !Array.isArray(payload.data)) return payload.data;
  return payload;
};

const getErrorMessage = (error: any, fallback: string) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.msg ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
};

const Banners: React.FC = () => {
  const params = useParams();
  const venueId = params.branchId;

  const [branches, setBranches] = React.useState<any[]>([]);
  const [selectValue, setSelectValue] = React.useState<
    string | number | (string | number)[]
  >("");
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<BannerRow | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | number | null>(
    null
  );

  const [search, setSearch] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);

  const [clients, setClients] = React.useState<any[]>([]);
  const [parentDialogOpen, setParentDialogOpen] = React.useState(false);
  const [selectedClient, setSelectedClient] = React.useState<
    (string | number)[]
  >([]);
  const [currentBannerId, setCurrentBannerId] = React.useState<
    string | number | null
  >(null);
  const [assignedClients, setAssignedClients] = React.useState<any[]>([]);
  const [viewDialogOpen, setViewDialogOpen] = React.useState(false);
  const [statusLoadingId, setStatusLoadingId] = React.useState<
    string | number | null
  >(null);

  const formFields: Field[] = [
    { name: "title", label: "Title", required: true },
    { name: "description", label: "Description" },
     {
      name: "priority",
      label: "Priority",
      customRender: (value, onChange) => (
        <CustomTextField
          label="Priority"
          value={value ?? ""}
          type="number"
          onChange={(e: any) => onChange(e.target.value)}
          fullWidth
        />
      ),
    },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: [
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "Inactive" },
      ],
    },
    {
      name: "image",
      label: "Image",
      colSpan: 2,
      customRender: (value, onChange, helpers) => {
        return (
          <Box>
            <CustomTextField
              label="Image"
              type="file"
              inputProps={{ accept: "image/*" }}
              onChange={(e: any) => {
                const file = e.target.files?.[0];
                onChange(file);
                helpers?.setFieldValue("image", file);
              }}
              fullWidth
            />
            {(() => {
              let previewUrl: string | undefined = undefined;
              if (value && value instanceof File) {
                previewUrl = URL.createObjectURL(value);
              } else if (typeof value === "string" && value) {
                previewUrl = value;
              } else if (editingRow && editingRow.imageUrl) {
                previewUrl = editingRow.imageUrl;
              }
              if (!previewUrl) return null;
              return (
                <Box sx={{ mt: 1 }}>
                  <img
                    src={previewUrl}
                    alt={editingRow?.title || "banner"}
                    style={{ width: 160, height: 80, objectFit: "cover" }}
                  />
                </Box>
              );
            })()}
          </Box>
        );
      },
    },
   
  ];

  const openEditForId = (id: string | number) => {
    const found = rows.find((r) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };
  const [imageViewOpen, setImageViewOpen] = React.useState(false);
  const [imageToViewUrl, setImageToViewUrl] = React.useState<string | null>(null);
  const [viewingImageTitle, setViewingImageTitle] =
    React.useState<string | null>(null);
  const getImageUrlFromRow = (r: BannerRow | undefined | null) => {
    if (!r) return undefined;
    return r.imageUrl || resolveImageUrl(r.raw);
  };

  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: async (id: number | string) => {
        setToDeleteId(id);
        setConfirmOpen(true);
      },
    },
    onView: {
      handler: async (id: number | string) => {
        const found = rows.find((r) => String(r.id) === String(id));
        const url = getImageUrlFromRow(found);
        if (found && url) {
          setImageToViewUrl(url);
          setViewingImageTitle(found.title || "Banner Image");
          setImageViewOpen(true);
        } else {
          toast.error("No image available for this banner.", {
            position: "top-right",
          });
        }
      },
    },
    // onView: {
    //   handler: async (id: number | string) => {
    //     setCurrentBannerId(id);
    //     try {
    //       const response = await GetAssignedClients(String(id));
    //       let assignedData = [];
    //       if (response && response.members) {
    //         assignedData = response.members;
    //       } else if (Array.isArray(response)) {
    //         assignedData = response;
    //       }
    //       setAssignedClients(Array.isArray(assignedData) ? assignedData : []);
    //       setViewDialogOpen(true);
    //     } catch (error) {
    //       console.error("Error fetching assigned clients:", error);
    //       toast.error("Failed to fetch assigned clients.", {
    //         position: "top-right",
    //       });
    //     }
    //   },
    // },
    // onParentIcon: {
    //   handler: async (id: string | number) => {
    //     setCurrentBannerId(id);
    //     const businessId = selectValue ? String(selectValue) : venueId;
    //     if (!businessId) {
    //       console.error("No business ID available");
    //       return;
    //     }
    //     try {
    //       const response = await getAllClients(businessId);
    //       let clientsData = [];
    //       if (response && response.users) {
    //         clientsData = response.users;
    //       } else if (response && response.data) {
    //         clientsData = response.data;
    //       } else if (Array.isArray(response)) {
    //         clientsData = response;
    //       }
    //       setClients(Array.isArray(clientsData) ? clientsData : []);
    //       console.log("Fetched clients:", clientsData);
    //       setParentDialogOpen(true);
    //     } catch (error) {
    //       console.error("Error fetching clients:", error);
    //       toast.error("Failed to fetch clients.", {
    //         position: "top-right",
    //       });
    //     }
    //   },
    // },
  });

  const [rows, setRows] = React.useState<BannerRow[]>([]);

  const handleToggleStatus = async (id: string | number) => {
    setStatusLoadingId(id);
    try {
      const response = await toggleBanner(id);
      const banner = unwrapBanner(response);
      if (!banner) {
        throw new Error("No banner returned from toggle response.");
      }
      const mapped = mapBannerRecord(banner);
      setRows((prev) =>
        prev.map((r) => (String(r.id) === String(mapped.id) ? mapped : r))
      );
      toast.success("Status updated", { position: "top-right" });
    } catch (error: any) {
      console.error("Error toggling banner status:", error);
      toast.error(getErrorMessage(error, "Failed to update status."), {
        position: "top-right",
      });
    } finally {
      setStatusLoadingId(null);
    }
  };
  React.useEffect(() => {
    const fetchBranches = async () => {
      try {
        if (!venueId) {
          console.error("No venue ID available, venueId:", venueId);
          setBranches([]);
          return;
        }

        const response = await getAllBranches(venueId);

        let branchesData = [];
        if (response && response.businesses) {
          branchesData = response.businesses;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        }
        const branches = Array.isArray(branchesData) ? branchesData : [];
        setBranches(branches);
        if (branches.length > 0 && !selectValue) {
          setSelectValue(branches[0].business_id);
        }
      } catch (error) {
        setBranches([]);
      }
    };

    if (venueId) {
      fetchBranches();
    } else {
      console.log("No venueId available, skipping branch fetch");
    }
  }, [venueId]);

  React.useEffect(() => {
    const loadBanners = async () => {
      setLoading(true);
      try {
        const response = await fetchBanners();
        const bannerList = pickBannerList(response);
        setRows(bannerList.map((item) => mapBannerRecord(item)));
      } catch (error: any) {
        console.error("Error fetching banners:", error);
        setRows([]);
        toast.error(getErrorMessage(error, "Failed to fetch banners."), {
          position: "top-right",
        });
      } finally {
        setLoading(false);
      }
    };

    loadBanners();
  }, [refetchTrigger]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <CustomLabel
          text="Banners"
          variant="h4"
          fontWeight="semi-bold"
          sx={{ mb: 2 }}
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
        //   selectConfig={{
        //     options: branches.map((branch) => ({
        //       label: branch.name || "Unknown Branch",
        //       value: branch.business_id,
        //     })),
        //     value: selectValue,
        //     onChange: (v: any) => {
        //       setSelectValue(v);
        //     },
        //     placeholder: "Select branch",
        //     size: "medium",
        //     searchable: false,
        //     multiSelect: false,
        //     sx: {
        //       backgroundColor: COLORS.WhiteBackground,
        //     },
        //   }}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              onClick: () => {
                setEditingRow(null);
                setEditOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: 36 },
                minWidth: { sm: 36 },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<BannerRow>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination={true}
          loading={loading}
          fields={[
            {
              key: "imageUrl",
              header: "Image",
              transform: (r: BannerRow) =>
                r.imageUrl ? (
                  <img
                    src={r.imageUrl}
                    alt={r.title}
                    style={{ width: 120, height: 60, objectFit: "cover" }}
                  />
                ) : (
                  "-"
                ),
            },
            { key: "title", header: "Title" },
            { key: "description", header: "Description" },
            {
              key: "priority",
              header: "Priority",
              transform: (r: BannerRow) =>
                r.priority !== undefined && r.priority !== null ? r.priority : "-",
            },
            {
              key: "status",
              header: "Status",
              transform: (r: BannerRow) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Switch
                    checked={Boolean(r.isActive)}
                    onChange={() => handleToggleStatus(r.id)}
                    color="primary"
                    inputProps={{ "aria-label": "status switch" }}
                    disabled={statusLoadingId === r.id}
                  />
                  <Typography sx={{ ml: 1 }}>
                    {r.status || (r.isActive ? "Active" : "Inactive")}
                  </Typography>
                </Box>
              ),
            },
          ]}
          actions={wiredActions}
          fetchFunc={async (params?: any) => {
            const q = (params && params.search) || search || "";

            const normalizedQuery = String(q || "").toLowerCase().trim();

            const filtered = rows.filter((r) => {
              if (!normalizedQuery) return true;

              const haystack = [r.title, r.description, r.status, r.priority ?? ""]
                .filter(
                  (value) => value !== undefined && value !== null && value !== ""
                )
                .map((value) => String(value).toLowerCase())
                .join(" ");

              return haystack.includes(normalizedQuery);
            });

            const content = filtered;
            const totalElements = content.length;
            const pageSize = (params && params.size) || 10;
            const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

            return {
              content,
              number: 0,
              size: pageSize,
              totalElements,
              totalPages,
            };
          }}
        />
        <GenericFormDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={editingRow ? `Edit Banner` : "Add Banner"}
          fields={editingRow ? formFields : [...formFields]}
          initialValues={
            editingRow
              ? {
                  title: editingRow.title || "",
                  description: editingRow.description || "",
                  priority:
                    editingRow.priority !== undefined
                      ? String(editingRow.priority)
                      : "",
                  status: editingRow.isActive ? "Active" : "Inactive",
                  image: "",
                }
              : {
                  status: "Inactive",
                }
          }
          onSubmit={async (values) => {
            try {
              const payload = {
                title: values.title?.trim() || "",
                description: values.description ?? "",
                priority:
                  values.priority !== undefined && values.priority !== ""
                    ? Number(values.priority)
                    : undefined,
                is_active: (values.status || "Inactive") === "Active",
              };
              const rawImageValue = values.image as unknown;
              const imageValue =
                rawImageValue && typeof rawImageValue === "object"
                  ? (rawImageValue as File | Blob)
                  : undefined;
              const file =
                imageValue && imageValue instanceof Blob ? imageValue : undefined;

              if (editingRow) {
                await updateBanner(editingRow.id, payload, file);
                toast.success("Banner updated successfully!", {
                  position: "top-right",
                });
              } else {
                await createBanner(payload, file);
                toast.success("Banner created successfully!", {
                  position: "top-right",
                });
              }
              setEditOpen(false);
              setEditingRow(null);
              setRefetchTrigger((prev) => prev + 1);
            } catch (error: any) {
              console.error("Error saving Banner:", error);
              toast.error(getErrorMessage(error, "Failed to save banner."), {
                position: "top-right",
              });
            }
          }}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setToDeleteId(null);
          }}
          title="Delete entry"
          message="Are you sure you want to delete this entry? This action cannot be undone."
          onConfirm={async () => {
            if (toDeleteId === null) return;
            try {
              await deleteBanner(toDeleteId);
              setRows((prev) =>
                prev.filter((r) => String(r.id) !== String(toDeleteId))
              );
              toast.success("Banner deleted successfully!", {
                position: "top-right",
              });
              setRefetchTrigger((prev) => prev + 1);
            } catch (error: any) {
              console.error("Error deleting Banner:", error);
              toast.error(getErrorMessage(error, "Failed to delete banner."), {
                position: "top-right",
              });
            } finally {
              setConfirmOpen(false);
              setToDeleteId(null);
            }
          }}
        />


        <CustomDialog
          open={imageViewOpen}
          onClose={() => {
            setImageViewOpen(false);
            setImageToViewUrl(null);
            setViewingImageTitle(null);
          }}
          title={viewingImageTitle || "Image"}
          paperSx={{ minWidth: { xs: "90vw", sm: 500 }, maxWidth: 900, width: "80vw" }}
          actions={
            <>
              <CustomButton
                label="Close"
                onClick={() => {
                  setImageViewOpen(false);
                  setImageToViewUrl(null);
                  setViewingImageTitle(null);
                }}
                bgColor={COLORS.MainColor}
                textColor={COLORS.WhiteText}
              />
            </>
          }
        >
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", px: 1 }}>
            {imageToViewUrl ? (
              <img
                src={imageToViewUrl}
                alt={viewingImageTitle || "image"}
                style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
              />
            ) : (
              <Typography>No image to display</Typography>
            )}
          </Box>
        </CustomDialog>

      </Box>
    </Box>
  );
};

export default Banners;
